<template>
  <div class="policy-page-wrapper">
    <WeCircularNavbar />

    <section class="policy-page" v-if="currentCountry">
      <div class="policy-container">
        <button class="back-button" type="button" @click="$router.back()">
          ← Back
        </button>

        <h1 class="page-title">
          {{ currentCountry }} - Policy instruments
          <br />
          and incentives
        </h1>

        <p class="page-intro">
          This section provides an overview of key national policy tools and incentive mechanisms supporting women-led
          enterprises, the circular economy, Industry 4.0 adoption and smart specialisation objectives.
        </p>

        <section v-for="section in sections" :key="section.id" class="section-block">
          <h2 class="section-title">
            {{ section.title }}
          </h2>

          <p class="section-description">
            {{ section.description }}
          </p>

          <div v-if="paginatedItems(section).length" class="cards-list">
            <article
              v-for="(item, index) in paginatedItems(section)"
              :key="section.id + '-' + index"
              class="policy-card"
            >
              <h3 class="card-title">
                {{ getField(item, ['Name of the programme', 'Programme name', 'Name']) || '-' }}
              </h3>

              <p class="card-summary">
                {{ getField(item, ['Program summary', 'Programme summary', 'Summary']) || '-' }}
              </p>

              <div class="card-meta">
                <div class="meta-item">
                  <span class="meta-label">Country/Region:</span>
                  <span class="meta-value">
                    {{ getField(item, ['Country/Region', 'Country', 'Region']) || currentCountry }}
                  </span>
                </div>

                <div class="meta-item">
                  <span class="meta-label">Website:</span>
                  <a
                    v-if="getField(item, ['Website', 'Link', 'URL'])"
                    :href="formatUrl(getField(item, ['Website', 'Link', 'URL']))"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="meta-link"
                  >
                    {{ cleanText(getField(item, ['Website', 'Link', 'URL'])) }}
                  </a>
                  <span v-else class="meta-value">-</span>
                </div>

                <div class="meta-item">
                  <span class="meta-label">Contact:</span>
                  <span class="meta-value">
                    {{ cleanText(getField(item, ['Contact', 'Contact person', 'Email'])) || '-' }}
                  </span>
                </div>
              </div>

              <a
                v-if="getField(item, ['Website', 'Link', 'URL'])"
                :href="formatUrl(getField(item, ['Website', 'Link', 'URL']))"
                target="_blank"
                rel="noopener noreferrer"
                class="learn-more"
              >
                Learn more
                <span aria-hidden="true">→</span>
              </a>
            </article>
          </div>

          <div v-else class="empty-state">
            No data available for this section.
          </div>

          <div v-if="getPageCount(section) > 1" class="pagination-dots">
            <button
              v-for="page in getPageCount(section)"
              :key="section.id + '-page-' + page"
              type="button"
              class="dot"
              :class="{ active: filters[section.id].page === page }"
              @click="filters[section.id].page = page"
            />
          </div>
        </section>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import { fundingData } from './policy_instruments_and_incentives'
import { taxationData } from './Policy_instruments_and_incentives_b_Taxation_and_financial_and_other_incentives'
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'

export default {
  name: 'PolicyInstruments',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },

  data() {
    return {
      selectedCountry: this.getCountryFromRoute(),
      filters: {
        funding: {
          search: '',
          page: 1,
          itemsPerPage: 6
        },
        taxation: {
          search: '',
          page: 1,
          itemsPerPage: 6
        }
      }
    }
  },

  computed: {
    currentCountry() {
      return this.selectedCountry
    },

    availableCountries() {
      const merged = new Set([...Object.keys(fundingData || {}), ...Object.keys(taxationData || {})])
      return Array.from(merged)
    },

    sections() {
      return [
        {
          id: 'funding',
          title: 'Funding and access to finance',
          description:
            'Explore national funding opportunities and financial instruments that enable innovation, growth and sustainable transformation.',
          items: this.getCountryItems(fundingData, this.currentCountry)
        },
        {
          id: 'taxation',
          title: 'Taxation-related, financial and other incentives',
          description:
            'Learn about national tax measures and incentive schemes designed to stimulate green investment and circular competitiveness.',
          items: this.getCountryItems(taxationData, this.currentCountry)
        }
      ]
    }
  },

  watch: {
    '$route.query.country'() {
      this.selectedCountry = this.getCountryFromRoute()
      this.resetFilters()
    }
  },

  methods: {
    getCountryFromRoute() {
      const country = this.$route.query.country || this.$route.params.country || 'Hungary'

      if (typeof country !== 'string') {
        return 'Hungary'
      }

      return country
    },

    getCountryItems(source, country) {
      if (!source || !country) {
        return []
      }

      if (source[country]) {
        return source[country]
      }

      const matchedKey = Object.keys(source).find(key => key.trim().toLowerCase() === country.trim().toLowerCase())

      return matchedKey ? source[matchedKey] : []
    },

    handleCountryChange() {
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          country: this.selectedCountry
        }
      })

      this.resetFilters()
    },

    resetFilters() {
      this.filters.funding.search = ''
      this.filters.funding.page = 1
      this.filters.taxation.search = ''
      this.filters.taxation.page = 1
    },

    cleanText(value) {
      return (value || '').toString().trim()
    },

    normalizeText(value) {
      return this.cleanText(value).toLowerCase()
    },

    formatUrl(url) {
      const trimmed = this.cleanText(url)

      if (!trimmed) {
        return '#'
      }

      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed
      }

      return `https://${trimmed}`
    },

    getField(item, possibleKeys = []) {
      for (const key of possibleKeys) {
        if (item && item[key] !== undefined && item[key] !== null && String(item[key]).trim() !== '') {
          return item[key]
        }
      }
      return ''
    },

    getFilteredItems(section) {
      const searchTerm = this.normalizeText(this.filters[section.id].search)

      if (!searchTerm) {
        return section.items || []
      }

      return (section.items || []).filter(item => {
        return (
          this.normalizeText(this.getField(item, ['Name of the programme', 'Programme name', 'Name'])).includes(
            searchTerm
          ) ||
          this.normalizeText(this.getField(item, ['Program summary', 'Programme summary', 'Summary'])).includes(
            searchTerm
          ) ||
          this.normalizeText(this.getField(item, ['Website', 'Link', 'URL'])).includes(searchTerm) ||
          this.normalizeText(this.getField(item, ['Contact', 'Contact person', 'Email'])).includes(searchTerm)
        )
      })
    },

    paginatedItems(section) {
      const items = this.getFilteredItems(section)
      const start = (this.filters[section.id].page - 1) * this.filters[section.id].itemsPerPage
      const end = start + this.filters[section.id].itemsPerPage

      return items.slice(start, end)
    },

    getPageCount(section) {
      return Math.max(1, Math.ceil(this.getFilteredItems(section).length / this.filters[section.id].itemsPerPage))
    }
  }
}
</script>

<style scoped>
.policy-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 22px 0 64px;
}

.policy-container {
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  padding: 0 38px;
  box-sizing: border-box;
}

.back-button {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin-bottom: 22px;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  margin-left: 0;
}

.page-title {
  margin: 0;
  max-width: 760px;
  color: #003399;
  line-height: 1.06;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: left;
}

.page-intro {
  max-width: 30%;
  margin: 14px 0 34px;
  color: #0e6eb6;
  line-height: 1.5;
  text-align: left;
}

.section-block {
  margin-top: 28px;
  text-align: left;
}

.section-title {
  margin: 0;
  max-width: 600px;
  color: #003399;
  line-height: 1.08;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.section-description {
  max-width: 30%;
  margin: 10px 0 70px;
  color: #0e6eb6;
  line-height: 1.5;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.policy-card {
  background: #004444;
  border-radius: 8px;
  padding: 14px 16px 12px;
  color: #ffffff;
}

.card-title {
  margin: 0;
  line-height: 1.35;
  font-weight: 700;
  color: white;
}

.card-summary {
  margin: 6px 0 12px;
  color: white;
  line-height: 1.5;
  max-width: 980px;
}

.card-meta {
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr;
  gap: 14px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.meta-label {
  line-height: 1.3;
  font-weight: 700;
  color: #ffffff;
}

.meta-value,
.meta-link {
  line-height: 1.4;
  color: white;
  text-decoration: none;
  word-break: break-word;
}

.meta-link:hover {
  text-decoration: underline;
}

.learn-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  color: #18baa8;
  text-decoration: none;
  font-weight: 700;
}

.learn-more:hover {
  text-decoration: underline;
}

.empty-state {
  padding: 18px 20px;
  border-radius: 8px;
  background: #e8edf4;
  color: #003399;
}

.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 7px;
  margin-top: 18px;
}

.dot {
  width: 11px;
  height: 11px;
  border: none;
  border-radius: 999px;
  background: #bcc6d8;
  padding: 0;
  cursor: pointer;
}

.dot.active {
  background: #18baa8;
}

@media (max-width: 900px) {
  .policy-container {
    padding: 0 20px;
  }

  .page-title {
    font-size: 26px;
  }

  .section-title {
    font-size: 21px;
  }

  .card-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .policy-page {
    padding-bottom: 48px;
  }

  .page-title {
    font-size: 22px;
  }

  .section-title {
    font-size: 18px;
  }

  .page-intro,
  .section-description {
    font-size: 12px;
  }

  .policy-card {
    padding: 14px;
  }

  .card-title {
    font-size: 12px;
  }

  .card-summary,
  .meta-label,
  .meta-value,
  .meta-link,
  .learn-more {
    font-size: 11px;
  }
}
</style>
